import apiClient from '../utils/axiosConfig';
import {
    clearAuthTokens,
    getAccessToken,
    storeAuthTokens
} from '../utils/authToken';
import {
    clearExplicitLogout,
    markExplicitLogout,
    refreshAuthTokens,
    resetAuthRefreshState,
    wasExplicitLogout,
} from '../utils/authSession';
import { getActionableErrorMessage } from '../utils/networkUx';
import {
    inactiveLoginFromResponse,
    INVALID_CREDENTIALS_MESSAGE,
} from '../utils/inactiveLogin';
import { clearSurveyRoutingCache } from '../utils/surveyEntry';
import { marketingSignupFields } from '../utils/publicAnalytics';
import { signupFieldErrors } from '../utils/signupFieldErrors';
import {
    LOGIN_SUCCESS,
    LOGIN_FAIL,
    USER_LOADED_SUCCESS,
    USER_LOADED_FAIL,
    AUTHENTICATED_FAIL,
    AUTHENTICATED_SUCCESS,
    PASSWORD_RESET_FAIL,
    PASSWORD_RESET_SUCCESS,
    PASSWORD_RESET_CONFIRM_FAIL,
    PASSWORD_RESET_CONFIRM_SUCCESS,
    SIGNUP_SUCCESS,
    SIGNUP_FAIL,
    ACTIVATION_SUCCESS,
    ACTIVATION_FAIL,
    GOOGLE_AUTH_SUCCESS,
    GOOGLE_AUTH_FAIL,
    LOGOUT
} from '../actions/types';


const getErrorMessage = (err) => {
    const actionable = getActionableErrorMessage(err, {
        subject: "authentication request",
        on429Subject: "sign-in attempts",
        generic: "We couldn't complete authentication. Please try again."
    });
    if (actionable) return actionable;
    if (err.response && err.response.data) {
        if (typeof err.response.data === 'string') return err.response.data;
        if (err.response.data.detail) return err.response.data.detail;
        return JSON.stringify(err.response.data);
    }
    if (err.message) return err.message;
    return "An unexpected error occurred.";
};

const ensureCsrfCookie = async () => {
    const response = await apiClient.get('/auth/csrf/');
    return response?.data?.csrfToken || null;
};

const isCsrfRejection = (err) => (
    Number(err?.response?.status || 0) === 403
    && String(err?.response?.data?.detail || '').toLowerCase().includes('csrf')
);

const isDefinitiveAuthFailure = (err) => {
    const status = Number(err?.response?.status || 0);
    const detail = `${err?.response?.data?.detail || ''} ${err?.response?.data?.code || ''}`.toLowerCase();
    return (
        status === 401 ||
        detail.includes('invalid refresh') ||
        detail.includes('token_not_valid') ||
        detail.includes('logged out')
    );
};


export const load_user = () => async dispatch => {
    let token = getAccessToken();

    if (!token) {
        if (wasExplicitLogout()) {
            dispatch({ type: USER_LOADED_FAIL });
            return { success: false };
        }
        try {
            await refreshAuthTokens();
            token = getAccessToken();
        } catch (err) {
            if (isDefinitiveAuthFailure(err)) {
                dispatch({ type: USER_LOADED_FAIL });
            }
            return { success: false };
        }
    }

    if (!token) {
        dispatch({ type: USER_LOADED_FAIL });
        return { success: false };
    }

    try {
        const res = await apiClient.get('/auth/users/me/');

        dispatch({
            type: USER_LOADED_SUCCESS,
            payload: res.data
        });

        return { success: true };

    } catch (err) {
        console.error("LOAD USER ERROR:", getErrorMessage(err));

        if (isDefinitiveAuthFailure(err)) {
            dispatch({ type: USER_LOADED_FAIL });
        }

        return {
            success: false,
            error: getErrorMessage(err)
        };
    }
};


export const checkAuthenticated = () => async dispatch => {
    let token = getAccessToken();

    if (!token) {
        if (wasExplicitLogout()) {
            dispatch({ type: AUTHENTICATED_FAIL });
            return { success: false };
        }
        try {
            await refreshAuthTokens();
            token = getAccessToken();
        } catch (err) {
            if (isDefinitiveAuthFailure(err)) {
                dispatch({ type: AUTHENTICATED_FAIL });
            }
            return { success: false };
        }
    }

    if (!token) {
        dispatch({ type: AUTHENTICATED_FAIL });
        return { success: false };
    }

    try {
        const res = await apiClient.post('/auth/jwt/verify/', { token });

        if (res.data.code !== 'token_not_valid') {
            dispatch({ type: AUTHENTICATED_SUCCESS });
            return { success: true };
        } else {
            dispatch({ type: AUTHENTICATED_FAIL });
            return { success: false };
        }

    } catch (err) {
        console.error("AUTH CHECK ERROR:", getErrorMessage(err));

        if (isDefinitiveAuthFailure(err)) {
            dispatch({ type: AUTHENTICATED_FAIL });
        }

        return {
            success: false,
            error: getErrorMessage(err)
        };
    }
};


export const login = (email, password) => async dispatch => {

    try {
        clearExplicitLogout();
        let csrfToken = await ensureCsrfCookie();
        let res;
        try {
            res = await apiClient.post(
                '/auth/jwt/create/',
                { email, password },
                { headers: csrfToken ? { 'X-CSRFToken': csrfToken } : {} }
            );
        } catch (err) {
            if (!isCsrfRejection(err)) throw err;
            csrfToken = await ensureCsrfCookie();
            res = await apiClient.post(
                '/auth/jwt/create/',
                { email, password },
                { headers: csrfToken ? { 'X-CSRFToken': csrfToken } : {} }
            );
        }
        storeAuthTokens(res.data);

        dispatch({
            type: LOGIN_SUCCESS,
            payload: res.data
        });

        await dispatch(load_user());

        return { success: true };

    } catch (err) {
        // A 401 from jwt/create is a rejected password, not an expired session.
        // The verify-email message is used only when the API sets
        // account_inactive after the password matched.
        const inactive = inactiveLoginFromResponse(err?.response?.data, email);
        const status = Number(err?.response?.status || 0);
        const error = inactive
            ? inactive.message
            : status === 401
                ? INVALID_CREDENTIALS_MESSAGE
                : getErrorMessage(err);
        console.error("LOGIN ERROR:", error);

        dispatch({ type: LOGIN_FAIL });

        return {
            success: false,
            error,
            ...(inactive ? { code: inactive.code, email: inactive.email } : {}),
        };
    }
};


export const signup = (first_name, last_name, email, password, re_password, referral_code = '') => async dispatch => {

    try {
        await ensureCsrfCookie();
        const payload = {
            first_name,
            last_name,
            email,
            password,
            re_password
        };
        if (referral_code) payload.referral_code = referral_code;
        const marketing = marketingSignupFields();
        if (marketing.anonymous_id) payload.anonymous_id = marketing.anonymous_id;
        if (marketing.attribution && Object.keys(marketing.attribution).length) {
            payload.attribution = marketing.attribution;
        }
        if (marketing.device) payload.device = marketing.device;
        if (marketing.viewport_bucket) payload.viewport_bucket = marketing.viewport_bucket;
        const res = await apiClient.post('/auth/users/', payload);

        dispatch({
            type: SIGNUP_SUCCESS,
            payload: res.data
        });

        return { success: true };

    } catch (err) {
        console.error("SIGNUP ERROR:", getErrorMessage(err));

        dispatch({ type: SIGNUP_FAIL });

        return {
            success: false,
            error: getErrorMessage(err),
            fields: signupFieldErrors(err?.response?.data, {
                status: Number(err?.response?.status || 0),
                network: !err?.response,
            }),
        };
    }
};


// Verify-email screen "Resend link". Djoser answers 204 for any address, and
// the API rate-limits this route per IP.
export const resendActivation = async (email) => {
    try {
        await ensureCsrfCookie();
    } catch {
        // The resend route does not require the app CSRF cookie.
    }
    try {
        await apiClient.post('/auth/users/resend_activation/', { email });
        return { success: true };
    } catch (err) {
        const status = Number(err?.response?.status || 0);
        return {
            success: false,
            rateLimited: status === 429 || status === 403,
            error: getErrorMessage(err),
        };
    }
};


// Same answer whether or not the account exists. The server limits repeats.
export const resendActivationQuiet = async (email) => {
    try {
        await ensureCsrfCookie();
    } catch {
        // This route does not require the app CSRF cookie.
    }
    try {
        await apiClient.post('/auth/reengage/resend-activation/', { email });
        return { success: true };
    } catch (err) {
        const status = Number(err?.response?.status || 0);
        return {
            success: false,
            rateLimited: status === 429 || status === 403,
        };
    }
};


export const verify = (uid, token, nextPath = "") => async dispatch => {

    try {
        const res = await apiClient.post('/auth/users/activation/', {
            uid,
            token,
            next: nextPath || "",
        });

        dispatch({ type: ACTIVATION_SUCCESS });

        const data = res?.data && typeof res.data === "object" ? res.data : {};
        return { success: true, data, status: res?.status };

    } catch (err) {
        console.error("ACTIVATION ERROR:", getErrorMessage(err));

        dispatch({ type: ACTIVATION_FAIL });

        return {
            success: false,
            error: getErrorMessage(err)
        };
    }
};


export const reset_password = (email) => async dispatch => {

    try {
        await ensureCsrfCookie();
        await apiClient.post('/auth/users/reset_password/', { email });

        dispatch({ type: PASSWORD_RESET_SUCCESS });

        return { success: true };

    } catch (err) {
        console.error("PASSWORD RESET ERROR:", getErrorMessage(err));

        dispatch({ type: PASSWORD_RESET_FAIL });

        return {
            success: false,
            error: getErrorMessage(err)
        };
    }
};


export const reset_password_confirm = (uid, token, new_password, re_new_password) => async dispatch => {

    try {
        await ensureCsrfCookie();
        await apiClient.post('/auth/users/reset_password_confirm/', {
            uid,
            token,
            new_password,
            re_new_password
        });

        dispatch({ type: PASSWORD_RESET_CONFIRM_SUCCESS });

        return { success: true };

    } catch (err) {
        console.error("PASSWORD RESET CONFIRM ERROR:", getErrorMessage(err));

        dispatch({ type: PASSWORD_RESET_CONFIRM_FAIL });

        return {
            success: false,
            error: getErrorMessage(err)
        };
    }
};


export const logout = () => async dispatch => {
    // Block silent cookie refresh immediately so login cannot auto-restore session.
    markExplicitLogout();
    resetAuthRefreshState();
    clearAuthTokens();

    try {
        let csrfToken = await ensureCsrfCookie();
        try {
            await apiClient.post(
                '/auth/jwt/logout/',
                {},
                { headers: csrfToken ? { 'X-CSRFToken': csrfToken } : {} }
            );
        } catch (err) {
            if (!isCsrfRejection(err)) throw err;
            csrfToken = await ensureCsrfCookie();
            await apiClient.post(
                '/auth/jwt/logout/',
                {},
                { headers: csrfToken ? { 'X-CSRFToken': csrfToken } : {} }
            );
        }
    } catch (err) {
        // Local logout must still complete if the server token is already gone.
    }

    // Clear again after the server clears HttpOnly refresh cookies.
    clearAuthTokens();
    clearSurveyRoutingCache();
    dispatch({ type: LOGOUT });
};
